/**
 * Wraps `document.startViewTransition` so an interrupted transition never
 * surfaces as an unhandled promise rejection.
 *
 * A View Transition is "skipped" whenever another transition starts before it
 * finishes, the page navigates away, or the browser cannot run it (common on
 * Android Chrome under compositing pressure). When that happens the transition's
 * `ready` promise rejects with `InvalidStateError: Transition was aborted
 * because of invalid state`. Nothing here awaits the transition, so without an
 * attached handler that rejection becomes an unhandled rejection.
 *
 * Falls back to running the callback directly where the API is unavailable.
 */
export const safeStartViewTransition = (callback: () => void): void => {
  if (typeof document === 'undefined' || !document.startViewTransition) {
    callback();
    return;
  }

  const transition = document.startViewTransition(callback);

  // Swallow the expected interruption rejections. These catches exist purely so
  // a skipped transition is not reported as an unhandled rejection — the
  // transition outcome itself is intentionally not awaited.
  transition.ready.catch(() => {});
  transition.finished.catch(() => {});
  transition.updateCallbackDone.catch(() => {});
};
